import { useEffect, useMemo, useState, useRef } from 'react';
import BoardTabs from '@/components/ui/BoardTabs/BoardTabs';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ColumnDef, FilterFn, flexRender } from '@tanstack/react-table';
import styles from './SupportedsPage.module.scss';

import useFundContext from '@/contexts/Fund/useFundContext';
import useDeviceDetection from '@/hooks/useDeviceDetection';
import useInquiriesFeature from '@/hooks/useInquiriesFeature';
import useFeature from '@/contexts/FeatureFlags/useFeature';
import SupportedDtoIn, { ContactClassificationDtoIn } from '@/models/in/SupportedDtoIn';
import { ContactClassificationStatus } from '@/models/ContactClassificationStatus';
import { getExpirationComment } from '@/helpers/contactClassificationExpiration';
import DataTable from '@/components/ui/DataTable/DataTable';
import SelectAllHeader from '@/components/ui/DataTable/SelectAllHeader';
import cellType from '@/components/ui/DataTable/CellTypes';
import { Cell, Col } from '@/components/ui/DataTable/ColumnsHelpers';
import useTableDensity from '@/hooks/useTableDensity';
import { useGetAuthApi, usePostAuthApi } from '@/hooks/UseApiCalls';
import type { RefreshableQueryMeta } from '@/hooks/useGlobalRefresh';
import OpenInquiryButton from '@/components/Inquiries/OpenInquiryButton';
import CustomMenu from '@/components/ui/Menu';
import FiltersArea from './FiltersArea';
import SupportedsRowActions from './SupportedsRowActions';
import RequestIsraeliIdChangeDialog from './RequestIsraeliIdChangeDialog/RequestIsraeliIdChangeDialog';
import { SUPPORTED_ID_CHANGE_REQUESTS_FEATURE_KEY } from '@/models/in/SupportedIsraeliIdChangeRequestDtoIn';
import useFundIsraeliIdChangeRequests from '@/hooks/useFundIsraeliIdChangeRequests';
import IsraeliIdChangeDot from './IsraeliIdChange/IsraeliIdChangeDot';
import IsraeliIdChangeBanner from './IsraeliIdChange/IsraeliIdChangeBanner';
import useSeenIsraeliIdChangeAlerts from '@/hooks/useSeenIsraeliIdChangeAlerts';
import { queryClient } from '@/hooks/QueryClient';
import AdditionalInfo from './AdditionalInfo/AdditionalInfo';
import CircleTextSkeleton from '@/components/ui/Skeleton/CircleTextSkeleton';
import Checkbox from '@/components/ui/Checkbox/Checkbox';
import { offerTaskSatisfaction } from '@/helpers/surveyStorage';
import Button from '@/components/ui/Button';
import NewSupported from '@/pages/newSupported/newSupported';
import NewSupportRequest from '@/pages/SupportRequests/NewSupportRequest';
import AddClassificationPopup from '@/pages/Supporteds/AddClassificationPopup/AddClassificationPopup';
import { FundStatus } from '@/models/common/FundStatus';
import { ContactDtoIn } from '@/models/in/ContactDtoIn';
import ListShortenedByCount from '@/components/ui/ListShortenedByCount/ListShortenedByCount';
import ClassificationConfirmationPopup from '@/components/ui/ClassificationConfirmationPopup/ClassificationConfirmationPopup';
import { ContactsClassificationDtoOut } from '@/models/out/ContactsClassificationDtoOut';
import FundBulkSupportedsDialog, { FUND_BULK_SUPPORTEDS_FEATURE_KEY } from '@/pages/Supporteds/FundBulkSupportedsDialog/FundBulkSupportedsDialog';
import AddChildrenPopup from '@/pages/Supporteds/AddChildrenPopup/AddChildrenPopup';
import AddPaymentInfoPopup from '@/pages/Supporteds/AddPaymentInfoPopup/AddPaymentInfoPopup';
import SupportCertificatePopup from '@/pages/Supporteds/SupportCertificatePopup/SupportCertificatePopup';
import DraftsButton from '@/pages/SupportRequests/Drafts/DraftsButton';
import { useDeleteSupportRequestDraft } from '@/pages/SupportRequests/Drafts/useSupportRequestDrafts';
import { SupportRequestDraftDtoIn } from '@/models/in/SupportRequestDraftDtoIn';
import {
  NEDARIM_DRAFTS_FEATURE_KEY,
  SUPPORTED_DRAFT_KIND,
} from '@/pages/newSupported/utils/useNewSupportedDraftsFeature';
import { describeSupportedDraft } from '@/pages/newSupported/utils/mapSupportedDraftToFormData';

const DENSITY_STORAGE_KEY = 'supportedsTableDensity';

const supportedSkeletonConfig = {
  goal: () => ({
    widths: ['102px'],
  }),
  supported: () => ({
    widths: ['88px'],
  }),
  sum: () => ({
    widths: ['73px'],
  }),
  paymentMethod: () => ({
    widths: ['60px'],
  }),
  date: () => ({
    widths: ['71px'],
  }),
  status: () => ({
    customRender: () => <CircleTextSkeleton circleSize={10} rows={[{ width: '56px', height: '10px' }]} gap={6} />,
  }),
}; //TODO: implement supportedSkeletonConfig

/**
 * Gets the ContactClassification to display in the table
 * Currently the rule is to return the first ContactClassification
 */
const GetFirstClassification = (contactClassifications: ContactClassificationDtoIn[]): ContactClassificationDtoIn | null => {
  return contactClassifications?.[0] || null;
};

const supportedSearch: FilterFn<SupportedDtoIn> = (row, _columnId, value) => {
  if (!value) return true;

  const searchValue = String(value).toLowerCase();

  const supported = row.original;

  // Search by full name (also matches first/last name separately)
  const normalizedSearchValue = searchValue.trim();
  const fullName = `${(supported.firstName ?? '').trim()} ${(supported.lastName ?? '').trim()}`.replace(/\s+/g, ' ').toLowerCase();
  return (
    fullName.includes(normalizedSearchValue) ||
    (supported.israeliId ?? '').toLowerCase().includes(normalizedSearchValue) ||
    (supported.email ?? '').toLowerCase().includes(normalizedSearchValue) ||
    (supported.phone ?? '').toLowerCase().includes(normalizedSearchValue)
  );
};

const SupportedsPage = () => {
  const { currentFund } = useFundContext();
  const { t } = useTranslation();
  const location = useLocation();
  const { isMobile } = useDeviceDetection();
  const hasInquiries = useInquiriesFeature();
  /*
   * רוחב עמודת הפעולות תלוי בדגל הפניות.
   *
   * כשהדגל כבוי OpenInquiryButton מחזיר null, אבל הרוחב הקבוע של העמודה נמדד
   * בזמנו כשהאייקון היה שם - ולכן נשארו 40px ריקים (34px כפתור + 6px רווח)
   * שדחפו את שאר האייקונים מהקצה. שתי המחרוזות כתובות במלואן כי Tailwind
   * מייצר מחלקות מהמקור ולא מערכים שמחושבים בזמן ריצה.
   */
  // HAT-1428: an ID that was typed wrong is the supported's identity, so asking the office to
  // correct it sits with the row's other actions, not only inside the details blade
  const hasIdChangeRequests = useFeature(SUPPORTED_ID_CHANGE_REQUESTS_FEATURE_KEY);
  const [supportedForIdChange, setSupportedForIdChange] = useState<SupportedDtoIn | null>(null);
  // A request waiting, or a decision not seen yet: a dot beside the supported's name, a filter chip
  // among the others, and a line above the board said once, whose «הצג» turns the chip on
  const { bySupported: idChangeRequestsBySupported } = useFundIsraeliIdChangeRequests();
  const openIdChanges = useMemo(() => [...idChangeRequestsBySupported.values()], [idChangeRequestsBySupported]);
  const [idChangeFilterOn, setIdChangeFilterOn] = useState(false);
  const { unseen: unseenIdChanges, markSeen: markIdChangesSeen } = useSeenIsraeliIdChangeAlerts('coordinator');
  const idChangesToTell = unseenIdChanges(openIdChanges);
  // The chip goes with the last open request, and so does its filter
  useEffect(() => {
    if (idChangeFilterOn && idChangeRequestsBySupported.size === 0) setIdChangeFilterOn(false);
  }, [idChangeFilterOn, idChangeRequestsBySupported]);
  // 40px per icon: children, payment, certificate - plus the inquiry and the ID request when on
  const rowActionsWidth = {
    3: 'max-w-[116px] min-w-[116px] w-[116px]',
    4: 'max-w-[156px] min-w-[156px] w-[156px]',
    5: 'max-w-[196px] min-w-[196px] w-[196px]',
  }[3 + (hasInquiries ? 1 : 0) + (hasIdChangeRequests ? 1 : 0)];
  const [selectedSupportedName, setSelectedSupportedName] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [density, setDensity] = useTableDensity(DENSITY_STORAGE_KEY, 'normal');
  const showIsraeliId = isMobile || density === 'airy';
  const [openNewSupported, setOpenNewSupported] = useState(false);
  /*
   * HAT-1453: what the add-supported success screen can lead on to. The request dialog opens with the
   * new supported already chosen; the classifications dialog opens for them. A frozen fund takes no
   * new requests (same rule as the support requests screen), so the first option is not offered.
   */
  const navigate = useNavigate();
  const [requestForSupported, setRequestForSupported] = useState<ContactDtoIn | null>(null);
  const [openNewRequest, setOpenNewRequest] = useState(false);
  const [classificationsForSupported, setClassificationsForSupported] = useState<ContactDtoIn | null>(null);
  const isFundFrozen = currentFund?.status === FundStatus.Frozen;
  const hasBulkUpload = useFeature(FUND_BULK_SUPPORTEDS_FEATURE_KEY);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  /* The draft the coordinator chose to finish, if they came in through DraftsButton. */
  const [continuedDraft, setContinuedDraft] = useState<SupportRequestDraftDtoIn | null>(null);
  const deleteSupportedDraft = useDeleteSupportRequestDraft(SUPPORTED_DRAFT_KIND);
  const [isClassificationPopupOpen, setIsClassificationPopupOpen] = useState(false);
  const [selectedSupportedIds, setSelectedSupportedIds] = useState<number[]>([]);
  const [hasSkippedSupporteds, setHasSkippedSupporteds] = useState(false);
  const [addChildrenPopupOpen, setAddChildrenPopupOpen] = useState(false);
  const [selectedSupportedForChildren, setSelectedSupportedForChildren] = useState<number | null>(null);
  const [addPaymentInfoPopupOpen, setAddPaymentInfoPopupOpen] = useState(false);
  const [selectedSupportedForPaymentInfo, setSelectedSupportedForPaymentInfo] = useState<number | null>(null);
  const [certificatePopupOpen, setCertificatePopupOpen] = useState(false);
  const [selectedSupportedForCertificate, setSelectedSupportedForCertificate] = useState<SupportedDtoIn | null>(null);

  const { data: serverSupporteds, isLoading, isError, refetch: refetchSupporteds } = useGetAuthApi<SupportedDtoIn[]>(`funds/${currentFund?.id}/supporteds`, undefined, {
    meta: { refreshable: true } satisfies RefreshableQueryMeta,
  });

  /*
   * HAT-1343: גוף תשובה שהוא JSON תקין אבל לא רשימה (אובייקט, מחרוזת) עובר את כל הבדיקות
   * ברמת הרשת - הסטטוס 200 וה-Content-Type הוא JSON - ומגיע לכאן. עד עכשיו הוא הוזרם כמו
   * שהוא ל-state, ו-FiltersArea קרס עליו ב-forEach והפיל את כל המסך. כאן זה נעצר: רק
   * רשימה נכנסת ל-state, וכל דבר אחר מוצג כמו כישלון טעינה, עם "נסו שוב".
   */
  const supportedsList = Array.isArray(serverSupporteds) ? serverSupporteds : undefined;
  const isMalformed = serverSupporteds !== undefined && !Array.isArray(serverSupporteds);
  useEffect(() => {
    if (isMalformed) {
      console.error(`Supporteds list for fund ${currentFund?.id} is not an array - showing the load-failed state instead:`, serverSupporteds);
    }
  }, [isMalformed, serverSupporteds, currentFund?.id]);

  const contactsClassificationMutation = usePostAuthApi<{ result: number }, ContactsClassificationDtoOut>(
    `funds/${currentFund?.id}/supporteds/classifications`
  );

  const [supporteds, setSupporteds] = useState<SupportedDtoIn[]>([]);
  const [filteredSupporteds, setFilteredSupporteds] = useState<SupportedDtoIn[]>([]);

  //* Any code (if necessary) for the AdditionalInfo component goes below
  const [detailedSupported, setDetailedSupported] = useState<SupportedDtoIn>();

  //* Any code (if necessary) for the AdditionalInfo component goes above

  const filtersRef = useRef<{ resetFilters: () => void }>(null);

  useEffect(() => {
    setSupporteds(supportedsList ?? []);
    setFilteredSupporteds(supportedsList ?? []);

    // Keep the open additional-info panel in sync after a refetch, without
    // listing detailedSupported as a dependency (that caused a setState loop).
    setDetailedSupported((prev) => {
      if (!prev || !supportedsList) return prev;
      return supportedsList.find((s) => s.id === prev.id) ?? prev;
    });
  }, [supportedsList]);

  useEffect(() => {
    setDetailedSupported(undefined);
    filtersRef.current?.resetFilters();
    setIdChangeFilterOn(false);
  }, [currentFund]);

  const [searchParams, setSearchParams] = useSearchParams();

  // Auto-select supported when navigating from support request page or via query param
  useEffect(() => {
    const supportedId = (location.state as { supportedId?: number })?.supportedId;
    const queryResourceId = searchParams.get('resourceId');
    const targetId = supportedId || (queryResourceId ? Number(queryResourceId) : null);
    if (targetId && supportedsList) {
      const supported = supportedsList.find((s) => s.id === targetId);
      if (supported) {
        setDetailedSupported(supported);
        window.history.replaceState({}, '');
        if (queryResourceId) {
          searchParams.delete('resourceId');
          setSearchParams(searchParams, { replace: true });
        }
      }
    }
  }, [supportedsList, location.state]);

  const handleOpenSupportedAdditionalInfo = async (supportedId: number) => {
    setOpenNewSupported(false);

    const { data: refreshedData } = await refetchSupporteds();

    // Set the detailed supported - useEffect will keep it synced on future refreshes
    const supported = Array.isArray(refreshedData) ? refreshedData.find((s) => s.id === supportedId) : undefined;
    if (supported) {
      setDetailedSupported(supported);
    }
  };

  const handleContactsClassificationClick = (selectedRows: SupportedDtoIn[]) => {
    // Filter supporteds that already have classifications
    const withExisting = selectedRows.filter((s) => s.contactClassifications.length > 0);
    const withoutExisting = selectedRows.filter((s) => s.contactClassifications.length === 0);

    setHasSkippedSupporteds(withExisting.length > 0);
    setSelectedSupportedIds(withoutExisting.map((s) => s.id));
    setIsClassificationPopupOpen(true);
  };

  const handleContactsClassificationSubmit = async () => {
    if (!currentFund) {
      console.error('Current fund is not available');
      return;
    }

    const requestBody: ContactsClassificationDtoOut = {
      ContactIds: selectedSupportedIds,
      ContactClassification: {
        classificationId: 1, // Basic classification
        documents: [],
      },
    };

    try {
      await contactsClassificationMutation.mutateAsync(requestBody);
      setIsClassificationPopupOpen(false);
      await refetchSupporteds();
      filtersRef.current?.resetFilters();
      offerTaskSatisfaction('classification');
    } catch (error) {
      console.error('Error creating contacts classifications:', error);
      alert(t('errorSettingClassification'));
    }
  };

  const columns: ColumnDef<SupportedDtoIn>[] = [
    //TODO: add columns for the table according to the design
    {
      id: 'select',
      header: ({ table }) => <SelectAllHeader table={table} />,
      cell: ({ row }) => <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} />,
      meta: {
        className: 'max-w-[48px] min-w-[48px] w-[48px]',
        hiddenOnMobile: true,
        lockLayout: true,
      },
    },
    {
      id: 'supported',
      header: Col(t('supported')),
      accessorFn: (row) => ({
        firstName: row.firstName,
        lastName: row.lastName,
        israeliId: row.israeliId,
      }),
      cell: (context) => {
        const idChangeRequest = idChangeRequestsBySupported.get(context.row.original.id);
        return (
          <div className="flex items-center gap-[6px] min-w-0">
            <div className="min-w-0">
              {flexRender(Cell<SupportedDtoIn>(cellType.SupportedWithIsraeliID, { showIsraeliId }), context)}
            </div>
            {idChangeRequest && <IsraeliIdChangeDot request={idChangeRequest} />}
          </div>
        );
      },
      sortingFn: (a, b) => {
        return (a.original.firstName ?? '').localeCompare(b.original.firstName ?? '');
      },
      meta: {
        className: 'max-w-[180px] min-w-[180px] w-[180px]',
        mobileClassName: 'max-w-[40%] min-w-[40%] w-[40%]',
      },
    },
    {
      id: 'classification',
      header: Col(t('classification')),
      accessorKey: 'contactClassifications',
      cell: ({ getValue, row }) => {
        const values = getValue() as ContactClassificationDtoIn[];
        if (!values || values.length === 0) {
          return <div></div>;
        }

        const firstClassification = GetFirstClassification(values);
        const firstValue = t(firstClassification?.classification?.name || '');
        const additionalCount = values.length > 1 ? values.length - 1 : 0;

        const tooltipContent =
          additionalCount > 0 ? (
            <div className="body-xs-sm-regular font-body-1">
              {values.map((contactClassification) => t(contactClassification.classification.name)).join(', ')}
            </div>
          ) : null;

        return (
          <ListShortenedByCount
            value={firstValue}
            count={additionalCount}
            tooltipId={additionalCount > 0 ? `classification-tooltip-${row.id}` : undefined}
            tooltipContent={tooltipContent}
          />
        );
      },
      sortingFn: (a, b) => {
        const aType = GetFirstClassification(a.original.contactClassifications)?.classification?.name || '';
        const bType = GetFirstClassification(b.original.contactClassifications)?.classification?.name || '';
        return aType.localeCompare(bType);
      },
      meta: {
        className: 'max-w-[120px] min-w-[120px] w-[120px]',
        mobileClassName: 'max-w-[22%] min-w-[22%] w-[22%]',
      },
    },
    {
      id: 'classificationExpiration',
      header: Col(t('classificationExpiration')),
      accessorFn: (row) => {
        const contactClassification = GetFirstClassification(row.contactClassifications);
        if (!contactClassification) return '';

        if (contactClassification.status === ContactClassificationStatus.PendingApproval) {
          return t('pendingApproval');
        }

        if (contactClassification.expiresTimestamp) {
          return new Date(contactClassification.expiresTimestamp).toLocaleDateString('he-IL', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
          });
        }

        return '';
      },
      cell: Cell(cellType.String),
      sortingFn: (a, b) => {
        const aContactClassification = GetFirstClassification(a.original.contactClassifications);
        const bContactClassification = GetFirstClassification(b.original.contactClassifications);

        const getSortValue = (contactClassification: ContactClassificationDtoIn | null) => {
          if (!contactClassification) return Number.MAX_SAFE_INTEGER;
          if (contactClassification.status === ContactClassificationStatus.PendingApproval) {
            return Number.MAX_SAFE_INTEGER;
          }
          return contactClassification.expiresTimestamp ? new Date(contactClassification.expiresTimestamp).getTime() : Number.MAX_SAFE_INTEGER;
        };

        return getSortValue(aContactClassification) - getSortValue(bContactClassification);
      },
      meta: {
        className: 'max-w-[120px] min-w-[120px] w-[120px]',
        hiddenOnMobile: true,
      },
    },
    {
      id: 'childrenCount',
      header: Col(t('childrenCount')),
      accessorKey: 'childrenCount',
      cell: Cell(cellType.Number),
      meta: {
        className: 'max-w-[90px] min-w-[90px] w-[90px]',
        style: { textAlign: 'center' },
        mobileClassName: 'max-w-[18%] min-w-[18%] w-[18%]',
      },
    },
    {
      id: 'email',
      header: Col(t('email')),
      accessorKey: 'email',
      cell: Cell(cellType.String),
      meta: {
        className: 'max-w-[100px] min-w-[72px] w-[100px]',
        hiddenOnMobile: true,
      },
    },
    {
      id: 'phone',
      header: Col(t('phone')),
      accessorKey: 'phone',
      cell: Cell(cellType.String),
      meta: {
        className: 'max-w-[88px] min-w-[72px] w-[88px]',
        hiddenOnMobile: true,
      },
    },
    {
      id: 'comments',
      header: Col(t('comments')),
      accessorFn: (row) => {
        return () => {
          const contactClassification = GetFirstClassification(row.contactClassifications);
          return contactClassification ? getExpirationComment(contactClassification, t) : '';
        };
      },
      cell: Cell(cellType.CommentWithRightIcon),
      meta: {
        className: 'min-w-[56px] max-w-[96px] w-[72px]',
        hiddenOnMobile: true,
      },
    },
    {
      id: 'rowActions',
      header: '',
      enableSorting: false,
      cell: ({ row }) => (
        <SupportedsRowActions
          supported={row.original}
          onAddChildren={(id) => {
            setSelectedSupportedForChildren(id);
            setAddChildrenPopupOpen(true);
          }}
          onAddPaymentInfo={(id) => {
            setSelectedSupportedForPaymentInfo(id);
            setAddPaymentInfoPopupOpen(true);
          }}
          onSupportCertificate={(supported) => {
            setSelectedSupportedForCertificate(supported);
            setCertificatePopupOpen(true);
          }}
          onRequestIdChange={hasIdChangeRequests ? setSupportedForIdChange : undefined}
        />
      ),
      meta: {
        className: rowActionsWidth,
        hiddenOnMobile: true,
        lockLayout: true,
      },
    },
    /*
     * בנייד לפניות עמודה משלה, ולכן היא מותנית גם בדגל: בלעדיו נשארה כאן
     * עמודה ריקה ברוחב 48px בקצה כל שורה - אותה תקלה שתוקנה בעמודת הפעולות
     * שבדסקטופ, רק במסך שבו יש פחות מקום להרשות אותה.
     */
    ...(isMobile && hasInquiries
      ? ([
          {
            id: 'inquiry',
            header: '',
            cell: ({ row }: { row: { original: SupportedDtoIn } }) => (
              <div className="flex justify-center">
                <OpenInquiryButton resourceType="Supported" resourceId={String(row.original.id)} size="large" />
              </div>
            ),
            meta: {
              className: 'max-w-[48px] min-w-[48px] w-[48px]',
            },
          },
        ] as ColumnDef<SupportedDtoIn>[])
      : []),
    ...(isMobile
      ? ([
          {
            id: 'mobileActions',
            header: () => '',
            enableSorting: false,
            cell: ({ row }: { row: { original: SupportedDtoIn } }) => (
              <CustomMenu
                items={[
                  {
                    text: t('children'),
                    onClick: () => {
                      setSelectedSupportedForChildren(row.original.id);
                      setAddChildrenPopupOpen(true);
                    },
                  },
                  {
                    text: t('paymentDetails'),
                    onClick: () => {
                      setSelectedSupportedForPaymentInfo(row.original.id);
                      setAddPaymentInfoPopupOpen(true);
                    },
                  },
                  {
                    text: t('supportCertificate.title'),
                    onClick: () => {
                      setSelectedSupportedForCertificate(row.original);
                      setCertificatePopupOpen(true);
                    },
                  },
                ]}
              />
            ),
            meta: {
              className: 'max-w-[48px] min-w-[48px] w-[48px]',
            },
          },
        ] as ColumnDef<SupportedDtoIn>[])
      : []),
  ];

  const exportColumns: ColumnDef<SupportedDtoIn>[] = [
    {
      id: 'supported',
      header: 'supported',
      accessorFn: (row) => `${row.firstName} ${row.lastName}`,
    },
    {
      id: 'israeliId',
      header: 'israeliId',
      accessorKey: 'israeliId',
    },
    {
      id: 'classification',
      header: 'classification',
      accessorFn: (row) => {
        if (!row.contactClassifications || row.contactClassifications.length === 0) return '';
        return row.contactClassifications.map((cc) => t(cc.classification.name)).join(', ');
      },
    },
    {
      id: 'classificationExpiration',
      header: 'classificationExpiration',
      accessorFn: (row) => {
        if (!row.contactClassifications || row.contactClassifications.length === 0) return '';

        return row.contactClassifications
          .map((cc) => {
            if (cc.status === ContactClassificationStatus.PendingApproval) {
              return t('pendingApproval');
            }
            if (cc.expiresTimestamp) {
              return new Date(cc.expiresTimestamp).toLocaleDateString('he-IL', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              });
            }
            return '';
          })
          .filter(Boolean)
          .join(', ');
      },
    },
    {
      id: 'childrenCount',
      header: 'childrenCount',
      accessorKey: 'childrenCount',
    },
    {
      id: 'email',
      header: 'email',
      accessorKey: 'email',
    },
    {
      id: 'phone',
      header: 'phone',
      accessorKey: 'phone',
    },
  ];

  //* Any code (if necessary) for the FiltersArea component goes below
  // Do not mind if you have to create a new state etc., just add it below.
  // Moving them upwards will be done in a separate step
  //* Any code (if necessary) for the FiltersArea component goes above

  return (
    <div>
      {currentFund ? (
        <>
          {/* ניווט הלוחות במסך צר (HAT-1170). מחזיר null מעל 1100. */}
          <BoardTabs />
          {detailedSupported && (
            <AdditionalInfo
              supported={detailedSupported}
              handleXclick={() => setDetailedSupported(undefined)}
              onSupportedNameClick={setSelectedSupportedName}
              onRefreshList={refetchSupporteds}
            />
          )}

          <DataTable
            title={t('supporteds')}
            data={idChangeFilterOn ? filteredSupporteds.filter((s) => idChangeRequestsBySupported.has(s.id)) : filteredSupporteds}
            notice={
              currentFund && idChangesToTell.length > 0 ? (
                <IsraeliIdChangeBanner
                  fundId={currentFund.id}
                  requests={idChangesToTell}
                  onShow={() => setIdChangeFilterOn(true)}
                  onSeen={markIdChangesSeen}
                />
              ) : undefined
            }
            getRowId={(row) => String(row.id)}
            isLoading={isLoading}
            columns={columns}
            exportColumns={exportColumns}
            exportFilename={t('supporteds')}
            skeletonConfig={supportedSkeletonConfig}
            /*
             * HAT-1343: עד כאן טבלה ריקה הייתה נראית אותו דבר בשלושה מצבים שונים - קרן בלי
             * נתמכים, סינון שמסתיר את כולם, ובקשה שנכשלה. במקרה השלישי המשתמש ראה מסך ריק
             * ולחץ שוב ושוב בלי שום רמז שמשהו השתבש. כישלון טעינה אומר את זה ומציע לנסות שוב.
             */
            emptyStateContent={
              isError || isMalformed
                ? {
                    title: t('supportedsLoadFailed'),
                    description: t('supportedsLoadFailedDescription'),
                    action: <Button onClick={() => refetchSupporteds()}>{t('retry')}</Button>,
                  }
                : supporteds.length > 0
                  ? { title: t('noSupportedsFound'), description: t('noSupportedsFoundDescription') }
                  : { title: t('noSupportedsInFund'), description: t('noSupportedsInFundDescription') }
            }
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            density={density}
            onDensityChange={setDensity}
            columnLayoutKey="supporteds"
            searchPlaceholder="searchSupported"
            searchTooltip="searchSupportedTooltip"
            filters={
              <div className="flex items-end max-[767px]:flex-col max-[767px]:items-stretch">
                <div className="flex-grow">
                  <FiltersArea
                    supporteds={supporteds ?? []}
                    setFilteredSupporteds={setFilteredSupporteds}
                    selectedSupportedName={selectedSupportedName}
                    setSelectedSupportedName={setSelectedSupportedName}
                    ref={filtersRef}
                    idChangeFilter={{
                      count: idChangeRequestsBySupported.size,
                      isSelected: idChangeFilterOn,
                      onToggle: () => setIdChangeFilterOn((on) => !on),
                    }}
                  />
                </div>
                {/* Supporteds whose details were typed in the Nedarim Plus personal area,
                    where the certificates cannot be attached. Renders nothing when the
                    feature is off or there are none. */}
                <DraftsButton
                  kind={SUPPORTED_DRAFT_KIND}
                  featureKey={NEDARIM_DRAFTS_FEATURE_KEY}
                  translations="newSupportedDrafts"
                  describe={describeSupportedDraft}
                  onContinue={(draft) => {
                    setContinuedDraft(draft);
                    setOpenNewSupported(true);
                  }}
                />
                {/* Not for a Welfare fund: it holds a single supported, so a file of many cannot be
                    what is wanted there, and the server refuses the second one anyway. */}
                {hasBulkUpload && currentFund?.classification !== 'Welfare' && (
                  <Button variant="outline" onClick={() => setBulkUploadOpen(true)} className={styles.addButton}>
                    {t('bulkUploads.uploadFromExcel')}
                  </Button>
                )}
                {!(currentFund?.classification === 'Welfare' && (supporteds?.length ?? 0) > 0) && (
                  <Button
                    variant="primary"
                    onClick={() => {
                      setContinuedDraft(null);
                      setOpenNewSupported(true);
                    }}
                    className={styles.addButton}>
                    {t('addSupported')}
                  </Button>
                )}
              </div>
            }
            handleRowClick={(row) => setDetailedSupported(row)}
            selectionActions={[
              {
                label: t('setBasicClassification'),
                onClick: handleContactsClassificationClick,
                isVisible: (selectedRows) => selectedRows.some((row) => row.contactClassifications.length === 0),
              },
            ]}
            activeRecord={detailedSupported}
            customGlobalSearch={supportedSearch}
          />
          {hasBulkUpload && currentFund && (
            <FundBulkSupportedsDialog fund={currentFund} open={bulkUploadOpen} setOpen={setBulkUploadOpen} onUploaded={refetchSupporteds} />
          )}
          <NewSupported
            open={openNewSupported}
            setOpen={(isOpen) => {
              setOpenNewSupported(isOpen);
              if (!isOpen) setContinuedDraft(null);
            }}
            onOpenSupportedAdditionalInfo={handleOpenSupportedAdditionalInfo}
            onRefreshList={refetchSupporteds}
            onContinueToRequest={
              isFundFrozen
                ? undefined
                : (supported) => {
                    setRequestForSupported(supported);
                    setOpenNewRequest(true);
                  }
            }
            onAddClassifications={(supported) => setClassificationsForSupported(supported)}
            draft={continuedDraft}
            onDraftCompleted={(draft) => {
              /* Best effort: the supported now exists, which is what the draft was for. */
              deleteSupportedDraft(draft.id);
              setContinuedDraft(null);
            }}
          />
          <NewSupportRequest
            open={openNewRequest}
            setOpen={setOpenNewRequest}
            initialSupported={requestForSupported}
            onInitialSupportedHandled={() => setRequestForSupported(null)}
            /* Once a request has been sent the coordinator belongs on the requests board, where it now is. */
            onRefreshList={() => currentFund && navigate(`/${currentFund.id}/support/support-requests`)}
          />
          <AddClassificationPopup
            fundId={currentFund?.id ?? 0}
            supportedId={classificationsForSupported?.id ?? 0}
            open={classificationsForSupported !== null}
            setOpen={(isOpen) => {
              if (!isOpen) setClassificationsForSupported(null);
            }}
            onRefreshList={refetchSupporteds}
            /* A classification added from the add-supported flow can lead on to a request, same as the add itself. */
            onGoToRequest={
              isFundFrozen || !classificationsForSupported
                ? undefined
                : () => {
                    setRequestForSupported(classificationsForSupported);
                    setOpenNewRequest(true);
                  }
            }
          />
          <ClassificationConfirmationPopup
            open={isClassificationPopupOpen}
            setOpen={setIsClassificationPopupOpen}
            selectedCount={selectedSupportedIds.length}
            onSubmit={handleContactsClassificationSubmit}
            isSubmitting={contactsClassificationMutation.isPending}
            classificationNames={[t('basic')]}
            hasSkippedSupporteds={hasSkippedSupporteds}
          />
          <AddChildrenPopup
            fundId={currentFund?.id ?? 0}
            supportedId={selectedSupportedForChildren ?? 0}
            open={addChildrenPopupOpen}
            setOpen={setAddChildrenPopupOpen}
            onRefreshList={refetchSupporteds}
          />
          <AddPaymentInfoPopup
            fundId={currentFund?.id ?? 0}
            supportedId={selectedSupportedForPaymentInfo ?? 0}
            open={addPaymentInfoPopupOpen}
            setOpen={setAddPaymentInfoPopupOpen}
            onRefreshList={refetchSupporteds}
          />
          <SupportCertificatePopup
            fundId={currentFund?.id ?? 0}
            supportedId={selectedSupportedForCertificate?.id ?? 0}
            supportedName={
              selectedSupportedForCertificate ? `${selectedSupportedForCertificate.firstName} ${selectedSupportedForCertificate.lastName}`.trim() : ''
            }
            open={certificatePopupOpen}
            setOpen={setCertificatePopupOpen}
          />
          {hasIdChangeRequests && supportedForIdChange && (
            <RequestIsraeliIdChangeDialog
              fundId={currentFund?.id ?? 0}
              supportedId={supportedForIdChange.id}
              supportedName={`${supportedForIdChange.firstName} ${supportedForIdChange.lastName}`.trim()}
              currentIsraeliId={supportedForIdChange.israeliId}
              open
              setOpen={(open) => {
                if (!open) setSupportedForIdChange(null);
              }}
              onRequested={() =>
                queryClient.invalidateQueries({
                  queryKey: [`funds/${currentFund?.id}/supporteds/${supportedForIdChange.id}/israeli-id-change-request`],
                })
              }
            />
          )}
        </>
      ) : (
        <h1>{t('noFundSelected')}.</h1>
      )}
    </div>
  );
};

export default SupportedsPage;
